import { Component, CUSTOM_ELEMENTS_SCHEMA, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ReplaySubject, takeUntil, firstValueFrom } from 'rxjs';
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-modal';
import '@maersk-global/mds-components-core/mc-icon';
import { ToastrService } from '../../services/toastr-service';
import { RepairCodeExclusionService } from '../../services/repair-code-exclusion.service';
import { RepairCodeExclusionDetailView } from '../../models/repair-code-exclusion-detail-view.model';
import { RepairCodeExclusionFormValue } from '../../form-model/create-repair-code-exclusion.form.model';
import { ErrorComponent } from '../../shared-components/error/error.component';
import { RepairCodeExclusionInformationComponent } from '../../components/create-repair-code-exclusion/repair-code-exclusion-information/repair-code-exclusion-information.component';
import { MasterDataAuditLogComponent } from '../../shared-components/master-data-audit-log/master-data-audit-log.component';
import {
  addExclusionFormControls,
  createExclusionRequest,
  createExclusionUpdate,
  getExclusionSubmitError,
  populateExclusionForm,
} from '../../helper/repair-code-exclusion-form-helpers';
import { markControlsAsTouched } from '../../helper/reactive-form-helpers';

@Component({
  selector: 'app-create-repair-code-exclusion',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    ErrorComponent,
    NgxSkeletonLoaderModule,
    RepairCodeExclusionInformationComponent,
    MasterDataAuditLogComponent,
  ],
  templateUrl: './create-repair-code-exclusion.component.html',
  styleUrls: ['./create-repair-code-exclusion.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CreateRepairCodeExclusionComponent implements OnInit, OnDestroy {
  formSubmitErrors = signal<string[]>([]);
  submitLoader = signal<boolean>(false);
  isEditMode = signal<boolean>(false);
  showCancelDialog = signal<boolean>(false);
  openDeleteRuleDialog = signal<boolean>(false);
  exclusiveRepairCodes = signal<string[]>([]);
  openAuditLog = false;
  repairCode: string | null = null;
  repairMode: string | null = null;
  detailData = signal<RepairCodeExclusionDetailView | null>(null);

  private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  form = this.formBuilder.group({});

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly toastrService: ToastrService,
    private readonly service: RepairCodeExclusionService,
  ) {}

  ngOnInit(): void {
    void this.initialize();
  }

  private async initialize(): Promise<void> {
    this.repairCode = this.route.snapshot.paramMap.get('repairCode');
    this.repairMode = this.route.snapshot.paramMap.get('repairMode');
    this.addControlsToTheFormGroup();
    if (this.repairCode && this.repairMode) {
      this.isEditMode.set(true);
      await this.loadDetailData(this.repairMode, this.repairCode);
    } else {
      this.isEditMode.set(false);
    }
  }

  private async loadDetailData(repairMode: string, repairCode: string): Promise<void> {
    try {
      const data = await firstValueFrom(this.service.getByRepairCodeAndMode(repairMode, repairCode));
      this.detailData.set(data);
      setTimeout(() => {
        this.populateFormWithData(data);
        this.form.get('repairCode')?.disable({ emitEvent: false });
        this.form.get('repairMode')?.disable({ emitEvent: false });
        this.form.get('description')?.disable({ emitEvent: false });
      });
    } catch {
      this.toastrService.showToastr('Failed to load repair code exclusion data', 'error');
      this.router.navigate(['/master-data/repair-code-exclusion']);
    }
  }

  private populateFormWithData(data: RepairCodeExclusionDetailView | null): void {
    if (!data) return;
    this.exclusiveRepairCodes.set(populateExclusionForm(this.form, data));
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private addControlsToTheFormGroup(): void {
    addExclusionFormControls(this.form, this.formBuilder);
  }

  addExclusiveRepairCode(): void {
    const control = this.form.get('exclusiveRepairCodeInput');
    const code = String(control?.value ?? '').trim();
    if (!code) {
      return;
    }

    const currentCodes = this.exclusiveRepairCodes();
    if (currentCodes.includes(code)) {
      this.toastrService.showToastr('Repair code already added', 'warning');
      return;
    }

    this.exclusiveRepairCodes.set([...currentCodes, code]);
    this.syncExclusiveCodesToForm();
    (control as any)?.setValue('');
    this.form.markAsDirty();
  }

  removeExclusiveRepairCode(code: string): void {
    const filtered = this.exclusiveRepairCodes().filter((x) => x !== code);
    this.exclusiveRepairCodes.set(filtered);
    this.syncExclusiveCodesToForm();
    this.form.markAsDirty();
  }

  private syncExclusiveCodesToForm(): void {
    const value = this.exclusiveRepairCodes().join(',');
    (this.form.get('exclusiveRepairCodes') as any)?.setValue(value);
    this.form.get('exclusiveRepairCodes')?.markAsTouched();
    this.form.get('exclusiveRepairCodes')?.updateValueAndValidity();
  }

  onCancel(): void {
    if (this.form.dirty) {
      this.showCancelDialog.set(true);
    } else {
      this.navigateBack();
    }
  }

  confirmCancel(): void {
    this.showCancelDialog.set(false);
    this.navigateBack();
  }

  keepEditing(): void {
    this.showCancelDialog.set(false);
  }

  private navigateBack(): void {
    if (this.isEditMode() && this.repairCode && this.repairMode) {
      this.router.navigate(['/master-data/repair-code-exclusion', this.repairMode, this.repairCode]);
      return;
    }
    this.router.navigate(['/master-data/repair-code-exclusion']);
  }

  onSubmit(): void {
    if (this.form.invalid) {
      markControlsAsTouched(this.form);
      this.toastrService.showToastr('Please fill in all required fields correctly', 'warning');
      return;
    }
    if (this.isEditMode()) {
      this.updateExclusion();
    } else {
      this.createExclusion();
    }
  }

  private createExclusion(): void {
    this.submitLoader.set(true);
    this.formSubmitErrors.set([]);
    const request = createExclusionRequest(this.form.getRawValue() as RepairCodeExclusionFormValue);
    this.service
      .create(request)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.submitLoader.set(false);
          this.form.reset();
          this.formSubmitErrors.set([]);
          this.toastrService.showToastr('Repair code exclusion added successfully', 'success');
          this.router.navigate(['/master-data/repair-code-exclusion']);
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error, 'creating');
        },
      });
  }

  private updateExclusion(): void {
    this.submitLoader.set(true);
    this.formSubmitErrors.set([]);
    const updatePayload = createExclusionUpdate(this.form.getRawValue() as RepairCodeExclusionFormValue);
    this.service
      .update(this.repairMode!, this.repairCode!, updatePayload)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.submitLoader.set(false);
          this.formSubmitErrors.set([]);
          this.toastrService.showToastr('Repair code exclusion updated successfully', 'success');
          this.router.navigate(['/master-data/repair-code-exclusion', this.repairMode, this.repairCode]);
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error, 'updating');
        },
      });
  }

  private handleError(error: HttpErrorResponse, operation: string): void {
    this.submitLoader.set(false);
    const { messages, toast } = getExclusionSubmitError(error, operation);
    this.formSubmitErrors.set(messages);
    this.toastrService.showToastr(toast, 'error');
  }

  onAuditLogClick(): void {
    if (!this.repairCode || !this.repairMode) {
      this.toastrService.showToastr('Repair code keys not available for audit log', 'error');
      return;
    }
    this.openAuditLog = true;
  }

  onDeleteRule(): void {
    if (!this.repairCode || !this.repairMode) {
      this.toastrService.showToastr('Repair code keys not available for delete', 'error');
      return;
    }

    this.submitLoader.set(true);
    this.service
      .delete(this.repairMode, this.repairCode)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.submitLoader.set(false);
          this.openDeleteRuleDialog.set(false);
          this.toastrService.showToastr('Repair code exclusion rule deleted successfully', 'success');
          this.router.navigate(['/master-data/repair-code-exclusion']);
        },
        error: () => {
          this.submitLoader.set(false);
          this.openDeleteRuleDialog.set(false);
          this.toastrService.showToastr('Failed to delete repair code exclusion rule', 'error');
        },
      });
  }

  readonly auditLogFetcher = (_transactionCode: string, isSystemLog: boolean, page: number, limit: number) =>
    this.service.getAuditLogs(this.repairMode!, this.repairCode!, isSystemLog, page, limit);
}
